import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "../../../components/AppIcon";
import Image from "../../../components/AppImage";
import Button from "../../../components/ui/Button";
import { useTranslation } from "react-i18next";

const OrderCard = ({
  order,
  onViewDetails,
  onTrackOrder,
  onReorder,
  onDownloadInvoice,
  onReturnItems,
  onCancelOrder,
}) => {
  const { t, i18n } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const navigate = useNavigate();

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case "delivered":
        return "bg-green-500 text-white";
      case "shipped":
        return "bg-blue-500 text-white";
      case "not_confirmed":
        return "bg-yellow-500 text-white";
      case "cancelled":
        return "bg-red-500 text-white";
      default:
        return "bg-gray-300 text-gray-700";
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat(i18n.language === "en" ? "en" : "ar", {
      style: "currency",
      currency: "IQD",
    }).format(price);
  };

  const handleReorder = () => {
    onReorder(order.id);
    navigate("/shopping-cart");
  };

  const handleCancelOrder = () => {
    onCancelOrder(order.id);
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-md hover:shadow-lg transition-shadow mb-4">
      {/* Order Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="relative">
              <Image
                src={order?.products[0]?.product?.images[0]?.file_path}
                alt={order.primaryProduct}
                className="w-12 h-12 rounded-lg object-cover"
              />
              {order.products?.length >= 1 && (
                <span className="absolute -top-2 -right-2 bg-blue-500 text-white text-xs font-medium rounded-full h-5 w-5 flex items-center justify-center">
                  {order?.products?.length}
                </span>
              )}
            </div>
            <div>
              <h3 className="font-semibold text-gray-800">
                {t("order")} #{order.order_number}
              </h3>
              <p className="text-sm text-gray-500">
                {formatDate(order?.created_at)}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <span
              className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(
                order?.status
              )}`}
            >
              {t(order.status.toLowerCase())}
            </span>
            <span className="font-semibold text-gray-800">
              {formatPrice(order?.total_price)}
            </span>
          </div>
        </div>
      </div>

      {/* Order Actions */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex flex-wrap gap-2">
          {order.status.toLowerCase() === "not_confirmed" && (
            <Button
              variant="outline"
              size="sm"
              iconName="X"
              iconPosition="left"
              onClick={handleCancelOrder}
            >
              {t("cancel")}
            </Button>
          )}

          {order.status.toLowerCase() === "delivered" && (
            <Button
              variant="outline"
              size="sm"
              iconName="RotateCcw"
              iconPosition="left"
              onClick={() => onReturnItems(order.id)}
            >
              {t("return")}
            </Button>
          )}

          <Button
            variant="ghost"
            size="sm"
            iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
            iconPosition="right"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {t("details")}
          </Button>
        </div>
      </div>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="p-4 bg-gray-100">
          <div className="space-y-4">
            {/* Items List */}
            <div>
              <h4 className="font-medium text-gray-800 mb-2">
                {t("items")} ({order.products.length})
              </h4>
              <div className="space-y-2">
                {order?.products?.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 p-2 bg-white rounded-lg shadow-sm"
                  >
                    <Image
                      src={item.product?.images[0]?.file_path}
                      alt={item.name}
                      className="w-10 h-10 rounded object-cover"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-800">
                        {item.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {t("quantity")}: {item?.quantity} ×{" "}
                        {formatPrice(
                          item?.product?.has_discount
                            ? item?.product?.price_after_discount
                            : item?.product?.price
                        )}
                      </p>
                    </div>
                    <span className="text-sm font-medium text-gray-800">
                      {item?.product?.has_discount
                        ? formatPrice(item?.quantity * item?.product?.price_after_discount)
                        : formatPrice(item?.quantity * item?.product?.price)}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Shipping Address */}
            <div>
              <h4 className="font-medium text-gray-800 mb-2">
                {t("shippingAddress")}
              </h4>
              <div className="text-sm text-gray-500">
                <p>{order?.address}</p>
                <p>{order?.province?.name}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderCard;
